import Swal from 'sweetalert2';

export const ValidMimeTypes = {
    CSV: ['text/csv', 'application/vnd.ms-excel'],
    XLS: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
}

/**
 * Valida un archivo CSV completo (MIME type + contenido)
 * Delimitador fijo: punto y coma (;)
 * 
 * @param file - Archivo a validar
 * @param minColumns - Número mínimo de columnas esperadas
 * @param maxColumns - Número máximo de columnas esperadas
 * @returns Promise que resuelve a true si el archivo es válido
 */
export const validateCSVFile = async (
    file,
    minColumns = 1,
    maxColumns = 1,
    validateHeaders = false,
    expectedHeaders = '',
    showLoadedFile = true
) => {
    Swal.fire({
        icon: 'info',
        title: 'Validando archivo...',
        html: 'Por favor espere mientras se termina la operación.',
        allowOutsideClick: false,
        allowEscapeKey: false
    });

    try {
        Swal.showLoading();

        // 1. Validar MIME type
        if (!file || !file.type)
            throw new Error('No se pudo determinar el tipo de archivo.');

        const validMimeTypes = ValidMimeTypes.CSV;

        const isValidMimeType = validMimeTypes.some((mimeType) => file.type === mimeType);

        if (!isValidMimeType)
            throw new Error(
                showLoadedFile
                    ? `El archivo no cumple con el formato establecido. Tipo detectado: ${file.type}`
                    : 'El archivo no cumple con el formato establecido.'
            );

        // 2. Leer y validar contenido
        const content = await file.text();

        if (!content || content.trim().length === 0)
            throw new Error('El archivo parece no tener datos. Por favor revíselo.');

        // 3. Dividir en filas (soporta \r\n, \n, \r)
        const rows = content.split(/\r?\n/).filter(row => row.trim().length > 0);
        
        if (validateHeaders) {
            const expectedHeaderArray = expectedHeaders.split(';').map(header => header.trim());
            const actualHeaderArray = rows[0].split(';').map(header => header.trim());

            if (expectedHeaderArray.length !== actualHeaderArray.length) {
                throw new Error(`Las cabeceras del archivo no coinciden con la plantilla. Por favor, descargue la plantilla y use el formato correcto.`);
            }

            if (rows.length < 2) {
                throw new Error(`El archivo debe contener al menos un registro, además de las cabeceras.`);
            }
        }

        // 4. Validar cada fila
        const delimiter = ';';

        for (let i = 0; i < rows.length; i++) {
            const columns = rows[i].split(delimiter).map(col => col.trim());
            const columnsFound = columns.length;

            if (columnsFound < minColumns)
                throw new Error(`<p>Se encontraron filas que contienen menos columnas a las requeridas, por favor revise el archivo.</p>`);

            if (columnsFound > maxColumns)
                throw new Error(`<p>Se encontraron filas que contienen más columnas de las requeridas, por favor revise el archivo.</p>`);
        }

        Swal.hideLoading();

        Swal.update({
            icon: 'info',
            title: null,
            html: 'La validación del archivo se completó con éxito. El proceso continuará en breve...',
            timer: 1500,
            showConfirmButton: false,
        });

        return true;
    } catch (error) {
        let message = error.message;

        if (error.name === 'NotReadableError')
            message = 'Verifique si tiene el archivo abierto en otro programa, o comuniquese con el soporte técnico.';

        Swal.hideLoading();

        Swal.update({
            icon: 'error',
            title: null,
            html: `<b style="color: red;">Advertencia</b>: ${message}`,
            showConfirmButton: true,
            confirmButtonText: 'Ok',
        });

        return false;
    }
};